import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Ship, Plus, Download, Trash2, Loader2, FileCode2, Info, Sparkles, AlertTriangle, Eye,
} from 'lucide-react';
import { shipmentsAPI, ics2API, pcfAPI } from '@/lib/api';
import { track, EVENTS } from '@/lib/telemetry';
import { toast } from 'sonner';

const TRANSPORT_MODES = [
  { value: '1', label: '1 — Sea' },
  { value: '2', label: '2 — Rail' },
  { value: '3', label: '3 — Road' },
  { value: '4', label: '4 — Air' },
  { value: '8', label: '8 — Inland waterway' },
];

const emptyParty = () => ({ eori: '', name: '', street: '', city: '', postcode: '', country: '' });
const emptyItem = (n = 1) => ({
  item_number: n, description: '', hs_code: '', gross_mass_kg: 0, net_mass_kg: null,
  quantity: null, quantity_unit: 'KGM', country_of_origin: '', cbam_embedded_co2_t: null,
});
const emptyForm = () => ({
  declaration_type: 'ENS', transport_mode: '1',
  conveyance_id: '', arrival_port: '', loading_port: '',
  estimated_arrival: new Date().toISOString().slice(0, 16),
  declarant: emptyParty(), consignor: emptyParty(), consignee: emptyParty(),
  goods: [emptyItem(1)],
});

export default function Shipments() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [preview, setPreview] = useState(null);
  const [pcfReports, setPcfReports] = useState([]);
  const [linking, setLinking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, pr] = await Promise.all([
        shipmentsAPI.list(),
        pcfAPI.listReports().catch(() => ({ data: [] })),
      ]);
      setList(r.data || []);
      setPcfReports(pr.data || []);
    } catch (e) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };

  const loadSample = async () => {
    try {
      const r = await ics2API.sample();
      const s = r.data;
      delete s.lrn; // let backend auto-generate
      s.estimated_arrival = (s.estimated_arrival || '').slice(0, 16);
      setForm(s);
      toast.success('Sample loaded into form');
    } catch {
      toast.error('Failed to load sample');
    }
  };

  const buildPayload = () => ({
    ...form,
    estimated_arrival: form.estimated_arrival
      ? new Date(form.estimated_arrival).toISOString()
      : new Date().toISOString(),
    goods: form.goods.map((g) => ({
      ...g,
      item_number: Number(g.item_number) || 1,
      gross_mass_kg: Number(g.gross_mass_kg) || 0,
      net_mass_kg: g.net_mass_kg === '' || g.net_mass_kg === null ? null : Number(g.net_mass_kg),
      quantity: g.quantity === '' || g.quantity === null ? null : Number(g.quantity),
      cbam_embedded_co2_t: g.cbam_embedded_co2_t === '' || g.cbam_embedded_co2_t === null
        ? null : Number(g.cbam_embedded_co2_t),
      country_of_origin: g.country_of_origin || null,
    })),
  });

  const saveShipment = async () => {
    setSaving(true);
    try {
      const res = await shipmentsAPI.create(buildPayload());
      track(EVENTS.SHIPMENT_CREATED, {
        mode: form.transport_mode,
        items: form.goods.length,
        lrn: res?.data?.lrn,
      });
      toast.success('Shipment created');
      setOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        ? JSON.stringify(e.response.data.detail).slice(0, 200)
        : 'Failed to create shipment');
    } finally {
      setSaving(false);
    }
  };

  const removeShipment = async (id) => {
    if (!window.confirm('Delete this shipment? This cannot be undone.')) return;
    try {
      await shipmentsAPI.delete(id);
      toast.success('Shipment deleted');
      setList((l) => l.filter((s) => s.id !== id));
    } catch {
      toast.error('Delete failed');
    }
  };

  const downloadXml = async (s) => {
    try {
      const res = await shipmentsAPI.generateXml(s.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ICS2_ENS_${s.lrn}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ICS2_ENS_${s.lrn}.xml`);
      await load();
    } catch {
      toast.error('XML generation failed');
    }
  };

  const previewXml = async (s) => {
    try {
      const res = await shipmentsAPI.previewXml(s.id);
      setPreview(res.data);
    } catch {
      toast.error('Preview failed');
    }
  };

  const linkReport = async (shipmentId, reportId) => {
    setLinking(shipmentId);
    try {
      const value = reportId === '__none__' ? null : reportId;
      await shipmentsAPI.linkReport(shipmentId, value);
      setList((lst) => lst.map((s) => s.id === shipmentId ? { ...s, linked_pcf_report: value } : s));
      toast.success(value ? 'PCF report linked' : 'PCF report unlinked');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link report');
    } finally {
      setLinking(null);
    }
  };

  const updatePartyField = (partyKey, field, value) => {
    setForm((f) => ({ ...f, [partyKey]: { ...f[partyKey], [field]: value } }));
  };
  const updateItem = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      goods: f.goods.map((g, i) => (i === idx ? { ...g, [field]: value } : g)),
    }));
  };
  const addItem = () => setForm((f) => ({ ...f, goods: [...f.goods, emptyItem(f.goods.length + 1)] }));
  const removeItem = (idx) => setForm((f) => ({
    ...f,
    goods: f.goods.filter((_, i) => i !== idx).map((g, i) => ({ ...g, item_number: i + 1 })),
  }));

  return (
    <div className="p-6 space-y-6" data-testid="shipments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ship className="w-6 h-6 text-primary" /> Shipments (ICS2 ENS)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Maintain draft Entry Summary Declarations and export ICS2-compliant XML for EU customs filings.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-shipment-btn">
          <Plus className="w-4 h-4 mr-2" /> New Shipment
        </Button>
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground rounded-md border border-border bg-muted/30 p-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Download the generated XML and upload it to your national customs portal
          (e.g. <b>DAKOSY</b> in Germany, <b>PLDA/CLOSE</b> in Belgium) — Veridex does not transmit filings directly.
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center" data-testid="shipments-empty">
          <Ship className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No shipments yet. Click "New Shipment" to create your first draft.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="shipments-table">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">LRN</th>
                  <th className="px-4 py-3 font-medium">Route</th>
                  <th className="px-4 py-3 font-medium">Mode</th>
                  <th className="px-4 py-3 font-medium">Consignee</th>
                  <th className="px-4 py-3 font-medium text-right">Items</th>
                  <th className="px-4 py-3 font-medium">Linked PCF</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((s) => (
                  <tr key={s.id} data-testid={`shipment-row-${s.id}`} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-mono text-xs">{s.lrn}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs">{s.loading_port}</span>
                      <span className="mx-1 text-muted-foreground">→</span>
                      <span className="font-mono text-xs">{s.arrival_port}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-[10px]">
                        {TRANSPORT_MODES.find((m) => m.value === s.transport_mode)?.label.split(' — ')[1] || s.transport_mode}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">{s.consignee?.name || '—'}</td>
                    <td className="px-4 py-3 text-right">{(s.goods || []).length}</td>
                    <td className="px-4 py-3 min-w-[200px]">
                      <Select
                        value={s.linked_pcf_report || '__none__'}
                        onValueChange={(v) => linkReport(s.id, v)}
                        disabled={linking === s.id}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`link-pcf-${s.id}`}>
                          <SelectValue placeholder="— not linked —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— not linked —</SelectItem>
                          {pcfReports.map((r) => {
                            const firstProduct = r.products?.[0]?.name;
                            const date = r.created_at ? new Date(r.created_at).toLocaleDateString() : '';
                            const label = firstProduct
                              ? `${firstProduct}${r.total_products > 1 ? ` +${r.total_products - 1}` : ''} — ${date}`
                              : `PCF ${r.id.slice(0, 8)} — ${date}`;
                            return (
                              <SelectItem key={r.id} value={r.id}>{label}</SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={s.status === 'xml_generated' ? 'default' : 'secondary'} className="text-[10px]">
                        {s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => previewXml(s)} data-testid={`preview-${s.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadXml(s)} data-testid={`download-${s.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeShipment(s.id)} data-testid={`delete-${s.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Preview modal */}
      {preview && (
        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-4xl" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileCode2 className="w-5 h-5" /> XML Preview — {preview.filename}</DialogTitle>
              <DialogDescription>
                {preview.size_bytes} bytes{preview.warnings?.length ? ` • ${preview.warnings.length} warning(s)` : ' • no warnings'}
              </DialogDescription>
            </DialogHeader>
            {preview.warnings?.length > 0 && (
              <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3 text-xs" data-testid="preview-warnings">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-4 h-4" /> Validation warnings
                </div>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <pre className="text-xs bg-muted/40 p-3 rounded border border-border overflow-auto max-h-[60vh]" data-testid="preview-xml">
              {preview.xml}
            </pre>
          </DialogContent>
        </Dialog>
      )}

      {/* New shipment dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="new-shipment-dialog">
          <DialogHeader>
            <DialogTitle>New ICS2 ENS shipment</DialogTitle>
            <DialogDescription>
              Fill declarant, consignor, consignee, transport details and at least one goods item.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={loadSample} data-testid="load-sample-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Load sample
            </Button>
          </div>

          <section data-testid="section-header">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Declaration header</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Declaration type</Label>
                <Select value={form.declaration_type} onValueChange={(v) => setForm({ ...form, declaration_type: v })}>
                  <SelectTrigger data-testid="field-declaration-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ENS">ENS — new</SelectItem>
                    <SelectItem value="ENS_AMEND">ENS_AMEND — amendment</SelectItem>
                    <SelectItem value="ENS_INVAL">ENS_INVAL — invalidation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Transport mode</Label>
                <Select value={form.transport_mode} onValueChange={(v) => setForm({ ...form, transport_mode: v })}>
                  <SelectTrigger data-testid="field-transport-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRANSPORT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <F label="Conveyance ID" value={form.conveyance_id} onChange={(v) => setForm({ ...form, conveyance_id: v })} t="field-conveyance" />
              <F label="Loading port (UN/LOCODE)" value={form.loading_port} onChange={(v) => setForm({ ...form, loading_port: v.toUpperCase() })} t="field-loading-port" maxLength={5} />
              <F label="Arrival port (UN/LOCODE)" value={form.arrival_port} onChange={(v) => setForm({ ...form, arrival_port: v.toUpperCase() })} t="field-arrival-port" maxLength={5} />
              <div>
                <Label className="text-xs">Estimated arrival</Label>
                <Input type="datetime-local" value={form.estimated_arrival} onChange={(e) => setForm({ ...form, estimated_arrival: e.target.value })} data-testid="field-eta" />
              </div>
            </div>
          </section>

          {[
            { key: 'declarant', title: 'Declarant' },
            { key: 'consignor', title: 'Consignor' },
            { key: 'consignee', title: 'Consignee' },
          ].map(({ key, title }) => (
            <section key={key} data-testid={`section-${key}`}>
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</h3>
              <div className="grid md:grid-cols-3 gap-3">
                <F label="EORI" value={form[key].eori} onChange={(v) => updatePartyField(key, 'eori', v)} t={`${key}-eori`} />
                <F label="Name" value={form[key].name} onChange={(v) => updatePartyField(key, 'name', v)} t={`${key}-name`} />
                <F label="Country (ISO-2)" value={form[key].country} onChange={(v) => updatePartyField(key, 'country', v.toUpperCase())} t={`${key}-country`} maxLength={2} />
                <F label="Street" value={form[key].street} onChange={(v) => updatePartyField(key, 'street', v)} t={`${key}-street`} />
                <F label="City" value={form[key].city} onChange={(v) => updatePartyField(key, 'city', v)} t={`${key}-city`} />
                <F label="Postcode" value={form[key].postcode} onChange={(v) => updatePartyField(key, 'postcode', v)} t={`${key}-postcode`} />
              </div>
            </section>
          ))}

          <section data-testid="section-goods">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Goods ({form.goods.length})</h3>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="w-4 h-4 mr-1" /> Add item</Button>
            </div>
            <div className="space-y-2">
              {form.goods.map((g, idx) => (
                <div key={idx} className="grid md:grid-cols-12 gap-2 border-b border-border pb-2" data-testid={`goods-row-${idx}`}>
                  <div className="md:col-span-1"><Label className="text-xs">#</Label><Input value={g.item_number} disabled /></div>
                  <div className="md:col-span-4"><Label className="text-xs">Description</Label><Input value={g.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} data-testid={`item-${idx}-desc`} /></div>
                  <div className="md:col-span-2"><Label className="text-xs">HS code</Label><Input value={g.hs_code} onChange={(e) => updateItem(idx, 'hs_code', e.target.value)} data-testid={`item-${idx}-hs`} /></div>
                  <div className="md:col-span-2"><Label className="text-xs">Gross mass (kg)</Label><Input type="number" value={g.gross_mass_kg} onChange={(e) => updateItem(idx, 'gross_mass_kg', e.target.value)} data-testid={`item-${idx}-gross`} /></div>
                  <div className="md:col-span-2"><Label className="text-xs">CBAM CO₂ (t)</Label><Input type="number" step="0.001" value={g.cbam_embedded_co2_t ?? ''} onChange={(e) => updateItem(idx, 'cbam_embedded_co2_t', e.target.value)} data-testid={`item-${idx}-cbam`} /></div>
                  <div className="md:col-span-1 flex items-end"><Button size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={form.goods.length === 1} data-testid={`remove-item-${idx}`}><Trash2 className="w-4 h-4" /></Button></div>
                  <div className="md:col-span-3"><Label className="text-xs">Origin (ISO-2)</Label><Input value={g.country_of_origin || ''} onChange={(e) => updateItem(idx, 'country_of_origin', e.target.value.toUpperCase())} maxLength={2} data-testid={`item-${idx}-origin`} /></div>
                  <div className="md:col-span-3"><Label className="text-xs">Net mass (kg)</Label><Input type="number" value={g.net_mass_kg ?? ''} onChange={(e) => updateItem(idx, 'net_mass_kg', e.target.value)} data-testid={`item-${idx}-net`} /></div>
                  <div className="md:col-span-3"><Label className="text-xs">Quantity</Label><Input type="number" value={g.quantity ?? ''} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} data-testid={`item-${idx}-qty`} /></div>
                  <div className="md:col-span-3"><Label className="text-xs">Unit</Label><Input value={g.quantity_unit || ''} onChange={(e) => updateItem(idx, 'quantity_unit', e.target.value.toUpperCase())} data-testid={`item-${idx}-unit`} /></div>
                </div>
              ))}
            </div>
          </section>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-shipment-btn">Cancel</Button>
            <Button onClick={saveShipment} disabled={saving} data-testid="save-shipment-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, value, onChange, t, maxLength }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={t} maxLength={maxLength} />
    </div>
  );
}
